import { CampfireActions, type CampfireAction } from "./actionNames";

/**
 * Default keyboard bindings per action.
 *
 * `code` matches `KeyboardEvent.code` (layout-independent — `KeyD` is the
 * physical D key on any keyboard). Modifier flags reflect the *required* state.
 * `metaOrCtrl` is `true` if either meta (mac) or ctrl (win/linux) is held; this
 * is the cross-platform flavour of the Unity multiselect/duplicate behavior.
 */

export interface KeyBinding {
  readonly code: string;
  readonly shift?: boolean;
  readonly alt?: boolean;
  readonly metaOrCtrl?: boolean;
}

export const DefaultActionBindings: Readonly<Record<CampfireAction, readonly KeyBinding[]>> = {
  [CampfireActions.Multiselect]: [
    { code: "ShiftLeft" },
    { code: "ShiftRight" },
  ],
  [CampfireActions.Rangeselect]: [
    { code: "ControlLeft" },
    { code: "ControlRight" },
    { code: "MetaLeft" },
    { code: "MetaRight" },
  ],
  [CampfireActions.SelectAll]: [{ code: "KeyA", metaOrCtrl: true }],
  [CampfireActions.Delete]: [{ code: "Delete" }, { code: "Backspace" }],
  // Campfire's copy/paste is its own in-memory entity buffer, not the OS
  // clipboard (Unity `DuplicationModel`), so these are ordinary bound actions.
  // `InputService` withholds every action while a text field has focus, which
  // is what keeps ⌘C/⌘V working normally inside the Spark prompt and rename
  // fields.
  [CampfireActions.Copy]: [{ code: "KeyC", metaOrCtrl: true }],
  [CampfireActions.Paste]: [{ code: "KeyV", metaOrCtrl: true }],
  [CampfireActions.Duplicate]: [{ code: "KeyD", metaOrCtrl: true }],
  [CampfireActions.Undo]: [{ code: "KeyZ", metaOrCtrl: true }],
  [CampfireActions.Redo]: [
    { code: "KeyZ", metaOrCtrl: true, shift: true },
    { code: "KeyY", metaOrCtrl: true },
  ],
  [CampfireActions.Submit]: [{ code: "Enter" }, { code: "NumpadEnter" }],
  [CampfireActions.Cancel]: [{ code: "Escape" }],
  [CampfireActions.FocusSelection]: [{ code: "KeyF" }],
  // Unity resets the scene camera on bare V. Require every modifier to be up
  // so paste (Ctrl/Cmd+V) cannot also trigger the reset.
  [CampfireActions.ResetCamera]: [
    { code: "KeyV", shift: false, alt: false, metaOrCtrl: false },
  ],
  [CampfireActions.ToggleHierarchy]: [{ code: "KeyH", metaOrCtrl: true }],
  [CampfireActions.ToggleInspector]: [{ code: "KeyI", metaOrCtrl: true }],
};
